import { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { CalendarDays } from 'lucide-react'
import type { WireArticle } from '../../api/types'

interface PublishingCadenceWidgetProps {
  articles: WireArticle[]
  embedded?: boolean
}

type ChartColors = {
  fg: string
  fgSecondary: string
  line: string
  tooltipBg: string
  tooltipFg: string
}

function resolveChartColors(): ChartColors {
  if (typeof window === 'undefined') {
    return {
      fg: '#111111',
      fgSecondary: '#6b7280',
      line: '#e5e7eb',
      tooltipBg: '#111111',
      tooltipFg: '#fcfcfc',
    }
  }
  const s = getComputedStyle(document.documentElement)
  const get = (v: string) => s.getPropertyValue(v).trim()
  return {
    fg: get('--gray-12') || '#111111',
    fgSecondary: get('--gray-11') || '#6b7280',
    line: get('--gray-6') || '#e5e7eb',
    tooltipBg: get('--gray-12') || '#111111',
    tooltipFg: get('--gray-1') || '#fcfcfc',
  }
}

function useChartTheme(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(resolveChartColors)
  useEffect(() => {
    const observer = new MutationObserver(() => setColors(resolveChartColors()))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  return colors
}

function ymd(date: Date): string {
  return date.toISOString().split('T')[0]
}

function mmdd(iso: string): string {
  const date = new Date(`${iso}T00:00:00.000Z`)
  if (Number.isNaN(date.getTime())) return iso
  return `${String(date.getUTCMonth() + 1).padStart(2, '0')}/${String(date.getUTCDate()).padStart(2, '0')}`
}

function upcomingDays(days: number): string[] {
  const out: string[] = []
  const start = new Date()
  start.setUTCHours(0, 0, 0, 0)
  for (let i = 0; i < days; i += 1) {
    const d = new Date(start)
    d.setUTCDate(start.getUTCDate() + i)
    out.push(ymd(d))
  }
  return out
}

export function PublishingCadenceWidget({ articles, embedded = false }: PublishingCadenceWidgetProps) {
  const colors = useChartTheme()

  const horizon = useMemo(() => upcomingDays(14), [])

  const countsByDay = useMemo(() => {
    const daySet = new Set(horizon)
    const map = new Map<string, number>()
    for (const day of horizon) map.set(day, 0)

    for (const article of articles) {
      const raw = article.workflow?.scheduledFor
      if (!raw) continue
      const ms = new Date(raw).getTime()
      if (!Number.isFinite(ms)) continue
      const day = ymd(new Date(ms))
      if (!daySet.has(day)) continue
      map.set(day, (map.get(day) ?? 0) + 1)
    }

    return map
  }, [articles, horizon])

  const dailyCounts = useMemo(() => horizon.map((day) => countsByDay.get(day) ?? 0), [horizon, countsByDay])
  const totalScheduled = useMemo(() => dailyCounts.reduce((sum, count) => sum + count, 0), [dailyCounts])
  const peak = useMemo(() => Math.max(0, ...dailyCounts), [dailyCounts])

  const option = useMemo(() => {
    if (horizon.length === 0) return null
    return {
      grid: { top: 12, right: 16, bottom: 40, left: 36 },
      tooltip: {
        trigger: 'axis' as const,
        backgroundColor: colors.tooltipBg,
        borderColor: colors.line,
        textStyle: { color: colors.tooltipFg, fontSize: 11 },
        formatter: (params: { axisValue: string; value: number }[]) => {
          const first = params[0]
          if (!first) return ''
          const count = Number(first.value) || 0
          return `${mmdd(first.axisValue)}<br/>${count} scheduled draft${count !== 1 ? 's' : ''}`
        },
      },
      xAxis: {
        type: 'category' as const,
        data: horizon,
        axisLabel: {
          color: colors.fg,
          fontSize: 11,
          fontWeight: 600,
          formatter: (value: string) => mmdd(value),
        },
        axisLine: { lineStyle: { color: colors.line } },
      },
      yAxis: {
        type: 'value' as const,
        minInterval: 1,
        axisLabel: { color: colors.fg, fontSize: 11, fontWeight: 600 },
        splitLine: { lineStyle: { color: colors.line } },
      },
      series: [
        {
          name: 'Scheduled drafts',
          type: 'bar' as const,
          barMaxWidth: 18,
          data: dailyCounts,
          itemStyle: { color: '#0ea5e9', borderRadius: [4, 4, 0, 0] },
        },
      ],
    }
  }, [horizon, dailyCounts, colors])

  const body = (
    <div className="space-y-2 px-4 py-4">
      {option ? (
        <div className="rounded-xl bg-canvas px-2 py-2">
          <ReactECharts option={option} style={{ height: 250 }} />
        </div>
      ) : (
        <div className="rounded-xl bg-canvas px-4 py-6 text-center text-[11px] text-fg-secondary">
          No schedule data available.
        </div>
      )}

      <div className="rounded-xl bg-fill/60 px-4 py-3 text-[11px] text-fg-secondary">
        {totalScheduled > 0
          ? `${totalScheduled} scheduled draft${totalScheduled !== 1 ? 's' : ''} in the next 14 days. Peak day: ${peak}.`
          : 'No drafts are currently scheduled in the next 14 days.'}
      </div>
    </div>
  )

  if (embedded) return <div>{body}</div>

  return (
    <section className="flex flex-col rounded border border-line bg-surface">
      <div className="flex items-center gap-2 border-b border-line px-5 py-3">
        <CalendarDays size={14} className="text-fg-secondary" />
        <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Publishing Cadence</span>
      </div>
      {body}
    </section>
  )
}
